import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useOrder } from "../context/OrderContext";

export default function OrderConfirmPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking, paid, expired, error
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);
  const order_ctx = useOrder();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      if (attempts.current >= 12) { setStatus("expired"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/orders/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data.order);
          setStatus("paid");
          order_ctx?.reset?.();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        setTimeout(poll, 2500);
      } catch {
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { stopped = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <Layout>
      <section className="page-section min-h-[80vh]" data-testid="order-confirm-page">
        <div className="max-w-xl mx-auto px-4 text-center">
          {status === "checking" && (
            <div className="card-soft p-10">
              <Loader2 className="w-12 h-12 mx-auto text-[#FF1F87] animate-spin" />
              <h1 className="font-display font-extrabold text-3xl mt-6">Confirming payment...</h1>
              <p className="text-[#4B5563] mt-2">Hang tight, this just takes a moment.</p>
            </div>
          )}

          {status === "paid" && order && (
            <div className="card-soft p-10 fade-in" data-testid="order-confirmed">
              <CheckCircle2 className="w-16 h-16 mx-auto text-[#FF1F87]" />
              <p className="text-sm uppercase tracking-[0.25em] font-bold text-[#FF1F87] mt-4">Order Confirmed</p>
              <h1 className="font-display font-extrabold text-4xl md:text-5xl mt-1">Thank You!</h1>
              <div className="mt-6 ss-gradient text-white rounded-2xl p-4 inline-block">
                <p className="text-xs uppercase tracking-widest font-bold opacity-90">Order Number</p>
                <p className="font-display font-extrabold text-2xl" data-testid="confirm-order-number">{order.order_number}</p>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3 text-left text-sm">
                <Info label="Method" value={order.method === "pickup" ? "Local Pickup" : "Local Delivery"} />
                <Info label="Pickup Date" value={order.pickup_date} />
                <Info label="Box Size" value={`${order.box_size} cookies`} />
                <Info label="Total Paid" value={`$${order.total.toFixed(2)}`} />
              </div>

              <div className="mt-4 text-left">
                <p className="text-xs uppercase tracking-widest text-[#FF1F87] font-bold">Flavors</p>
                <ul className="text-sm mt-1">
                  {order.items.map((it, i) => <li key={i}>{it.quantity}× {it.flavor_name}</li>)}
                </ul>
              </div>

              <Link to="/" className="btn-pink mt-8 inline-flex">BACK TO HOME</Link>
            </div>
          )}

          {status === "expired" && (
            <div className="card-soft p-10">
              <XCircle className="w-12 h-12 mx-auto text-[#FF1F87]" />
              <h1 className="font-display font-extrabold text-3xl mt-4">Payment Pending</h1>
              <p className="text-[#4B5563] mt-2">We couldn't confirm your payment automatically. Please check your email or contact us.</p>
              <Link to="/" className="btn-pink mt-6 inline-flex">BACK TO HOME</Link>
            </div>
          )}

          {status === "error" && (
            <div className="card-soft p-10">
              <XCircle className="w-12 h-12 mx-auto text-[#FF1F87]" />
              <h1 className="font-display font-extrabold text-3xl mt-4">Missing Session</h1>
              <p className="text-[#4B5563] mt-2">No payment session was found.</p>
              <Link to="/order" className="btn-pink mt-6 inline-flex">START NEW ORDER</Link>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-100 p-3 bg-white">
      <p className="text-[10px] uppercase tracking-widest text-[#FF1F87] font-bold">{label}</p>
      <p className="font-bold mt-1">{value}</p>
    </div>
  );
}
